/**
 * Shared benchmark math used by Cash Flow, Profit & Loss, and Balance Sheet
 * resolvers. Pure functions; no statement-specific policy.
 */

export const VOLATILITY_RELATIVE_THRESHOLD = 0.25;
export const VOLATILITY_PERCENTAGE_POINT_THRESHOLD = 15;

function isTtmYear(year) {
    return String(year || '').trim().toUpperCase() === 'TTM'
        || /^TTM/i.test(String(year || ''));
}

export function collectAnnualPoints(values, years) {
    const listValues = Array.isArray(values) ? values : [];
    const listYears = Array.isArray(years) && years.length
        ? years
        : listValues.map((_, index) => String(2000 + index));
    const length = Math.min(listValues.length, listYears.length) || Math.max(listValues.length, listYears.length);
    const points = [];
    for (let i = 0; i < length; i += 1) {
        const year = listYears[i] != null ? listYears[i] : String(2000 + i);
        const value = listValues[i];
        if (isTtmYear(year)) continue;
        if (value == null || !Number.isFinite(value)) continue;
        points.push({ year: String(year), value, index: i });
    }
    points.sort((a, b) => a.year.localeCompare(b.year));
    return points;
}

export function median(numbers) {
    if (!numbers.length) return null;
    const sorted = numbers.slice().sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) {
        return (sorted[mid - 1] + sorted[mid]) / 2;
    }
    return sorted[mid];
}

/**
 * Median of the most recent `requiredWindow` valid annual observations.
 *
 * @param {Array<number|null|undefined>} values
 * @param {string[]} [years]
 * @param {number} requiredWindow
 * @returns {number|null}
 */
export function calculateRollingMedian(values, years, requiredWindow) {
    const windowSize = Number(requiredWindow) || 0;
    const points = collectAnnualPoints(values, years);
    if (windowSize <= 0 || points.length < windowSize) return null;
    return median(points.slice(-windowSize).map((point) => point.value));
}

/**
 * Share of finite values that are strictly positive.
 *
 * @param {Array<number|null|undefined>} values
 * @returns {number|null}
 */
export function calculatePositiveYearsRatio(values) {
    const valid = (Array.isArray(values) ? values : []).filter((value) => value != null && Number.isFinite(value));
    if (!valid.length) return null;
    return valid.filter((value) => value > 0).length / valid.length;
}

function isPercentageSpec(spec) {
    return spec?.unit === 'percentage' || spec?.unit === 'pp';
}

function formatMetricNumber(value, spec) {
    if (value == null || !Number.isFinite(value)) return 'n/a';
    if (spec?.unit === 'pp') {
        const rounded = Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));
        return `${rounded}pp`;
    }
    if (spec?.unit === 'percentage') {
        const rounded = Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1)));
        return `${rounded}%`;
    }
    if (Number.isInteger(value)) return `${value}x`;
    return `${Number(value.toFixed(2))}x`;
}

function justBelow(strong, spec) {
    if (isPercentageSpec(spec) && Number.isInteger(strong)) return strong - 1;
    const step = 0.01;
    return Number((strong - step).toFixed(2));
}

function justAbove(strong, spec) {
    if (isPercentageSpec(spec) && Number.isInteger(strong)) return strong + 1;
    return Number((strong + 0.01).toFixed(2));
}

/**
 * @param {string} metricId
 * @param {object} spec
 * @param {number} requiredWindow
 * @param {{ label?: string }} [options]
 */
export function formatThresholdRule(metricId, spec, requiredWindow, options = {}) {
    if (!spec || !spec.thresholds) return '';
    const windowLabel = requiredWindow === 5 ? '5-year median' : '3-year median';
    const label = options.label || metricId;
    const t = spec.thresholds;
    if (spec.direction === 'higherIsBetter') {
        const watchHigh = formatMetricNumber(justBelow(t.strong, spec), spec);
        return `Strong: ${label} ≥ ${formatMetricNumber(t.strong, spec)}; Watch: ${formatMetricNumber(t.watch, spec)}–${watchHigh}; evaluated on ${windowLabel}.`;
    }
    if (spec.direction === 'lowerIsBetter') {
        const watchLow = formatMetricNumber(justAbove(t.strong, spec), spec);
        return `Strong: ${label} ≤ ${formatMetricNumber(t.strong, spec)}; Watch: ${watchLow}–${formatMetricNumber(t.watch, spec)}; evaluated on ${windowLabel}.`;
    }
    if (spec.direction === 'band') {
        const strongRange = `${formatMetricNumber(t.strongMin, spec)} to ${formatMetricNumber(t.strongMax, spec)}`;
        if (t.watchMin === t.strongMin && t.watchMax === t.strongMax) {
            return `Normal band: ${label} ${strongRange}; evaluated on ${windowLabel}.`;
        }
        const watchLow = t.watchMin === t.strongMin ? justAbove(t.strongMax, spec) : t.watchMin;
        return `Strong: ${label} ${strongRange}; Watch: ${formatMetricNumber(watchLow, spec)} to ${formatMetricNumber(t.watchMax, spec)}; evaluated on ${windowLabel}.`;
    }
    return `Evaluated on ${windowLabel}.`;
}

export function applyThreshold(value, spec) {
    if (value == null || !Number.isFinite(value) || !spec) return null;
    const t = spec.thresholds || {};
    if (spec.direction === 'higherIsBetter') {
        if (value >= t.strong) return 'strong';
        if (value >= t.watch) return 'watch';
        return 'weak';
    }
    if (spec.direction === 'lowerIsBetter') {
        if (value <= t.strong) return 'strong';
        if (value <= t.watch) return 'watch';
        return 'weak';
    }
    if (spec.direction === 'band') {
        if (value >= t.strongMin && value <= t.strongMax) return 'strong';
        if (value >= t.watchMin && value <= t.watchMax) return 'watch';
        return 'weak';
    }
    return null;
}

/**
 * @param {number|null} latest
 * @param {number|null} primary
 * @param {object} [spec]
 */
export function isVolatile(latest, primary, spec) {
    if (latest == null || primary == null || !Number.isFinite(latest) || !Number.isFinite(primary)) return false;
    if (isPercentageSpec(spec) && Math.abs(latest - primary) > VOLATILITY_PERCENTAGE_POINT_THRESHOLD) {
        return true;
    }
    const denom = Math.abs(primary);
    if (denom === 0) return Math.abs(latest - primary) > 0;
    return Math.abs(latest - primary) / denom > VOLATILITY_RELATIVE_THRESHOLD;
}

export function copyAligned(values, years) {
    return {
        values: Array.isArray(values) ? values.slice() : [],
        years: Array.isArray(years) ? years.slice() : [],
    };
}
